import { ActiveElement, Chart, ChartDataset, ChartEvent } from 'chart.js/auto';
import { CoreLabelOptions } from 'chartjs-plugin-annotation';
import Color from 'color';

export const COLORS: string[] = ['#050299', '#C22A98', '#5100D4', '#528FF0', '#A81208'];

export function defaultsDeep(target: { [x: string]: any }, ...sources: any[]) {
    sources.forEach((source) => {
        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                if (isObject(target[key]) && isObject(source[key])) {
                    defaultsDeep(target[key], source[key]);
                } else if (target[key] === undefined) {
                    target[key] = source[key];
                }
            }
        }
    });
    return target;
}

function isObject(obj: unknown) {
    return obj && typeof obj === 'object' && !Array.isArray(obj);
}

export function splitStringIntoChunks(
    string: string,
    maxLength: number,
    maxRows: number = Number.MAX_SAFE_INTEGER
): string[] {
    const WORDS: string[] = string.split(/\s+/);
    const CHUNKS: string[] = [];
    let currentChunk: string[] = [];
    let currentLength: number = 0;

    for (const word of WORDS) {
        if (word.length > maxLength) {
            if (currentChunk.length > 0) {
                CHUNKS.push(currentChunk.join(' '));
                currentChunk = [];
                currentLength = 0;
            }

            for (let i = 0; i < word.length; i += maxLength) {
                CHUNKS.push(word.substring(i, i + maxLength));
                if (CHUNKS.length >= maxRows) break;
            }
            continue;
        }

        if (currentLength + word.length + (currentChunk.length > 0 ? 1 : 0) > maxLength) {
            CHUNKS.push(currentChunk.join(' '));
            currentChunk = [];
            currentLength = 0;
        }

        currentChunk.push(word);
        currentLength += word.length + (currentChunk.length > 1 ? 1 : 0);

        if (CHUNKS.length >= maxRows) break;
    }

    if (currentChunk.length && CHUNKS.length < maxRows) {
        CHUNKS.push(currentChunk.join(' '));
    }

    if (
        CHUNKS.length > maxRows ||
        WORDS.length > CHUNKS.join(' ').split(/\s+/).length ||
        CHUNKS.reduce(
            (total: number, value: string) => total + value.replace(/ /g, '').length,
            0
        ) < string.replace(/ /g, '').length
    ) {
        CHUNKS.length = maxRows;
        CHUNKS[maxRows - 1] = CHUNKS[maxRows - 1]?.substring(0, maxLength - 3) + '...';
    }

    return CHUNKS;
}

export function updateCenterLabelOnHover(
    _: ChartEvent,
    chartElement: ActiveElement[],
    chart: Chart,
    defaultCenterLabel: string | string[],
    split: number = 0,
    maxRows: number = Number.MAX_SAFE_INTEGER
) {
    const ORIGINAL_ANNOTATION: string | string[] = (
        (
            chart!.config!.options!.plugins!.annotation!.annotations as Record<
                string,
                CoreLabelOptions
            >
        )['label1']!.content! as string[]
    ).join('');

    if (chartElement?.length) {
        let annotation: string[] = [
            // @ts-expect-error ignore this error
            chart.config.data.labels[
                (chart.config.data.datasets[chartElement[0].datasetIndex - 1]?.data
                    ?.length || 0) + chartElement[0].index
            ] as string
        ];
        if (split > 0) {
            annotation = splitStringIntoChunks(
                // @ts-expect-error ignore this error
                chart.config.data.labels[
                    (chart.config.data.datasets[chartElement[0].datasetIndex - 1]?.data
                        ?.length || 0) + chartElement[0].index
                ] as string,
                split,
                maxRows
            ) as string[];
        }

        (
            chart!.config!.options!.plugins!.annotation!.annotations as Record<
                string,
                CoreLabelOptions
            >
        )['label1']!.content = [
            `${
                chart.config.data.datasets[chartElement[0].datasetIndex].data[
                    chartElement[0].index
                ]
            }%`,
            ...annotation
        ];
    } else {
        (
            chart!.config!.options!.plugins!.annotation!.annotations as Record<
                string,
                CoreLabelOptions
            >
        )['label1']!.content = Array.isArray(defaultCenterLabel)
            ? defaultCenterLabel
            : [defaultCenterLabel];
    }

    if (
        ORIGINAL_ANNOTATION !==
        (
            (
                chart!.config!.options!.plugins!.annotation!.annotations as Record<
                    string,
                    CoreLabelOptions
                >
            )['label1']!.content! as string[]
        ).join('')
    ) {
        chart.update();
    }
}

export function updateElementsColorOnHover(
    _: ChartEvent,
    elements: ActiveElement[],
    chart: Chart,
    cachedColors: ChartDataset[]
): void {
    if (elements.length > 0) {
        const { index, datasetIndex } = elements[0];

        chart.data.datasets.forEach((dataset: ChartDataset, _datasetIndex: number) => {
            dataset.backgroundColor = (dataset.backgroundColor as string[]).map(
                (_: string, _index: number) => {
                    const COLOR: string = (
                        cachedColors[_datasetIndex].backgroundColor as string[]
                    )[_index];

                    if (datasetIndex === _datasetIndex && _index === index) {
                        return COLOR;
                    }
                    return Color(COLOR).fade(0.7).toString();
                }
            );
        });
    } else {
        chart.data.datasets.forEach((dataset: ChartDataset, _index: number) => {
            dataset.backgroundColor = cachedColors[_index].backgroundColor;
        });
    }

    chart.update();
}
